import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Sparkles, CheckCircle2, Filter } from "lucide-react";
import { GET_ALL_CHANGELOGS, MARK_ALL_CHANGELOGS_AS_READ } from "../../graphql/queries";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  category: string;
  importance: string;
  publishedAt: string;
  isRead: boolean;
}

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'FEATURE':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'IMPROVEMENT':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'BUGFIX':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'ANNOUNCEMENT':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'FEATURE':
      return 'Nowa funkcja';
    case 'IMPROVEMENT':
      return 'Ulepszenie';
    case 'BUGFIX':
      return 'Naprawa';
    case 'ANNOUNCEMENT':
      return 'Ogłoszenie';
    default:
      return category;
  }
};

export const ChangelogPage: React.FC = () => {
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const { data, loading, refetch } = useQuery<{ changelogs: ChangelogEntry[] }>(
    GET_ALL_CHANGELOGS,
    {
      fetchPolicy: 'network-only',
    }
  );

  const [markAllAsRead] = useMutation(MARK_ALL_CHANGELOGS_AS_READ, {
    onCompleted: () => {
      toast.success("Wszystkie zmiany oznaczone jako przeczytane");
      refetch();
    },
  });

  const changelogs = data?.changelogs || [];

  // Apply filters
  const filteredChangelogs = changelogs.filter(changelog => {
    if (filterCategory && changelog.category !== filterCategory) return false;
    if (showUnreadOnly && changelog.isRead) return false;
    return true;
  });

  const unreadCount = changelogs.filter(c => !c.isRead).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-primary" />
            Historia zmian
          </h2>
          <p className="text-muted-foreground mt-2">
            Zobacz wszystkie aktualizacje i nowe funkcje systemu
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={() => markAllAsRead()} variant="outline" className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Oznacz wszystko jako przeczytane ({unreadCount})
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtry
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap">
            <Button
              variant={filterCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterCategory(null)}
            >
              Wszystkie
            </Button>
            <Button
              variant={filterCategory === 'FEATURE' ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterCategory('FEATURE')}
            >
              Nowe funkcje
            </Button>
            <Button
              variant={filterCategory === 'IMPROVEMENT' ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterCategory('IMPROVEMENT')}
            >
              Ulepszenia
            </Button>
            <Button
              variant={filterCategory === 'BUGFIX' ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterCategory('BUGFIX')}
            >
              Naprawy
            </Button>
            <div className="flex-1" />
            <Button
              variant={showUnreadOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            >
              Tylko nieprzeczytane
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div className="space-y-4">
        {filteredChangelogs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Brak zmian spełniających kryteria filtrowania
            </CardContent>
          </Card>
        ) : (
          filteredChangelogs.map((changelog) => (
            <Card
              key={changelog.id}
              className={`${!changelog.isRead ? 'ring-2 ring-primary/30 bg-primary/5' : ''}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getCategoryColor(changelog.category)}>
                        {getCategoryLabel(changelog.category)}
                      </Badge>
                      <Badge variant="outline">v{changelog.version}</Badge>
                      {!changelog.isRead && (
                        <Badge className="bg-primary text-primary-foreground">
                          Nowe
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl">{changelog.title}</CardTitle>
                    <CardDescription>
                      {new Date(changelog.publishedAt).toLocaleDateString('pl-PL', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{changelog.description}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
